import React, { useState, useEffect, useCallback, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useSnackbar } from "notistack";

import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import Divider from "@mui/material/Divider";
import Typography from "@mui/material/Typography";
import TextField from "@mui/material/TextField";
import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import IconButton from "@mui/material/IconButton";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import DeleteForeverIcon from "@mui/icons-material/DeleteForever";
import { alpha } from "@mui/material/styles";

import LoadingDialog from "src/components/LoadingDialog";
import Confirm from "src/components/Confirm";
import Hint from "src/components/Hint";
import { getAircraftOperationHistory, hideAircraftOperationHistoryEntry, patchHistoryComment } from "src/api/aircraft";
import { SettingKey } from "src/shared/api-types/user";
import type { UserAircraftOperationHistoryEntry } from "src/shared/api-types/aircraft";
import type { AssignedLimitation } from "src/shared/api-types/limitations";
import { captureHandledException } from "src/services/sentry";

type DiffState = "added" | "removed" | "unchanged";
interface LimitationDiff {
  limitation: AssignedLimitation;
  state: DiffState;
}

const INITIAL_HISTORY_STATE: Pick<UserAircraftOperationHistoryEntry, "limitations"> = {
  limitations: [],
};

const getLimitationKey = (limitation: AssignedLimitation): string =>
  `${limitation.definitionId}:${limitation.optionId}`;

const getLimitationDiffs = (current: AssignedLimitation[], previous: AssignedLimitation[] = []): LimitationDiff[] => {
  const previousByKey = new Map(previous.map((limitation) => [getLimitationKey(limitation), limitation]));
  const currentByKey = new Map(current.map((limitation) => [getLimitationKey(limitation), limitation]));
  const diffs: LimitationDiff[] = current.map((limitation) => {
    const previousLimitation = previousByKey.get(getLimitationKey(limitation));
    const changed = previousLimitation && previousLimitation.optionId !== limitation.optionId;

    if (!previousLimitation) return { limitation, state: "added" };
    if (changed) return { limitation, state: "added" };
    return { limitation, state: "unchanged" };
  });

  previous.forEach((limitation) => {
    const currentLimitation = currentByKey.get(getLimitationKey(limitation));
    const removed = !currentLimitation || currentLimitation.optionId !== limitation.optionId;

    if (removed) {
      diffs.push({ limitation, state: "removed" });
    }
  });

  return diffs;
};

interface HistoryDialogProps {
  open: boolean;
  onClose: () => void;
  userId: number;
  crewId: number;
  typeId: number;
  operationId: number;
  operationName: string;
  canEdit: boolean;
}

const HistoryDialog: React.FC<HistoryDialogProps> = ({
  open,
  onClose,
  userId,
  crewId,
  typeId,
  operationId,
  operationName,
  canEdit,
}) => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const [history, setHistory] = useState<UserAircraftOperationHistoryEntry[]>([]);
  const [editingComments, setEditingComments] = useState<Record<number, string>>({});
  const [isLoading, setIsLoading] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [hidingIds, setHidingIds] = useState<Set<number>>(() => new Set());
  const [highlightedHistoryId, setHighlightedHistoryId] = useState<number | null>(null);
  const [loadedHistoryKey, setLoadedHistoryKey] = useState<string | null>(null);
  const [includeHidden, setIncludeHidden] = useState(false);
  const [confirmHideId, setConfirmHideId] = useState<number | null>(null);
  // Track which comment ids were changed so we only PATCH those on close
  const dirtyIds = useRef<Set<number>>(new Set());
  const historyKey = `${userId}:${crewId}:${typeId}:${operationId}`;

  const load = useCallback(
    async (refresh = false) => {
      if (!open) {
        setHistory([]);
        setEditingComments({});
        setIsLoading(false);
        setIsRefreshing(false);
        setLoadedHistoryKey(null);
        return;
      }
      if (!refresh) {
        dirtyIds.current.clear();
      }
      if (refresh) {
        setIsRefreshing(true);
      } else {
        setIsLoading(true);
        setLoadedHistoryKey(null);
      }
      try {
        const entries = await getAircraftOperationHistory(
          { userId, crewId, typeId, operationId },
          { includeHidden: true },
        );
        setHistory(entries);
        setEditingComments((prev) => {
          const serverComments = Object.fromEntries(entries.map((e) => [e.id, e.comment ?? ""]));
          if (!refresh) return serverComments;

          const next = { ...serverComments };
          dirtyIds.current.forEach((id) => {
            next[id] = prev[id] ?? "";
          });
          return next;
        });
        setLoadedHistoryKey(historyKey);
      } catch (err) {
        captureHandledException(err, {
          feature: "aircraftOperationHistory",
          action: "load",
          userId,
          crewId,
          typeId,
          operationId,
          refresh,
        });
        enqueueSnackbar(t("aircraft_management.load_history_failed"), { variant: "error" });
      } finally {
        if (refresh) {
          setIsRefreshing(false);
        } else {
          setIsLoading(false);
        }
      }
    },
    [open, userId, crewId, typeId, operationId, historyKey, t, enqueueSnackbar],
  );

  useEffect(() => {
    load(loadedHistoryKey !== null);
    // loadedHistoryKey intentionally excluded so loading completion does not trigger another refresh.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [load]);

  const handleCommentChange = (id: number, value: string) => {
    dirtyIds.current.add(id);
    setEditingComments((prev) => ({ ...prev, [id]: value }));
  };

  const handleClose = async () => {
    if (canEdit) {
      const saves = Array.from(dirtyIds.current).map((id) =>
        patchHistoryComment({ crewId, historyId: id }, { comment: editingComments[id] ?? "" }).catch((err) => {
          captureHandledException(err, {
            feature: "aircraftOperationHistory",
            action: "saveComment",
            crewId,
            historyId: id,
          });
          enqueueSnackbar(t("aircraft_management.save_comment_failed"), { variant: "error" });
        }),
      );
      await Promise.all(saves);
      dirtyIds.current.clear();
    }
    onClose();
  };

  const handleHideEntry = async (historyId: number) => {
    setConfirmHideId(null);
    setHidingIds((prev) => new Set(prev).add(historyId));
    try {
      await hideAircraftOperationHistoryEntry({ crewId, historyId });
      await load(true);
    } catch (err) {
      enqueueSnackbar(t("aircraft_management.hide_history_failed"), { variant: "error" });
    } finally {
      setHidingIds((prev) => {
        const next = new Set(prev);
        next.delete(historyId);
        return next;
      });
    }
  };

  const renderedHistory = includeHidden ? history : history.filter((entry) => !entry.hiddenAt);
  const hasHiddenHistory = history.some((entry) => entry.hiddenAt);

  return (
    <>
      <LoadingDialog
        open={open}
        onClose={handleClose}
        loading={isLoading}
        loadingMessage={t("loading")}
        closeLabel={t("close")}
        title={
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1 }}>
            <span>
              {t("aircraft_management.history_dialog_title", {
                operation: operationName,
                interpolation: { escapeValue: false },
              })}
            </span>
            {hasHiddenHistory && (
              <IconButton
                size="small"
                disabled={isRefreshing}
                title={t(
                  includeHidden ? "aircraft_management.hide_hidden_history" : "aircraft_management.show_hidden_history",
                )}
                onClick={() => setIncludeHidden((value) => !value)}
              >
                {includeHidden ? <VisibilityOffIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
              </IconButton>
            )}
          </Box>
        }
        disableRestoreFocus
      >
        <List disablePadding>
          {history.length > 0 && (
            <ListItem sx={{ px: 0, py: 0, mb: 1 }}>
              <Hint settingKey={SettingKey.AIRCRAFT_OPERATION_HISTORY_HINT} sx={{ width: "100%" }}>
                {t("aircraft_management.history_boundary_hint")}
              </Hint>
            </ListItem>
          )}
          {renderedHistory.length === 0 ? (
            <ListItem>
              <Typography
                variant="body2"
                sx={{
                  color: "text.secondary",
                }}
              >
                {t("aircraft_management.no_history")}
              </Typography>
            </ListItem>
          ) : (
            renderedHistory.map((entry, idx) => {
              const previous = renderedHistory[idx + 1] ?? INITIAL_HISTORY_STATE;
              const limitationDiffs = getLimitationDiffs(entry.limitations, previous?.limitations);
              const isHidden = Boolean(entry.hiddenAt);
              const historyIndex = history.findIndex((item) => item.id === entry.id);
              const canHideEntry = canEdit && !isHidden && historyIndex > 0 && historyIndex < history.length - 1;

              return (
                <React.Fragment key={entry.id}>
                  {idx > 0 && <Divider component="li" sx={{ borderColor: "divider", borderBottomWidth: 2, my: 1 }} />}
                  <ListItem
                    key={entry.id}
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "flex-start",
                      py: 1.5,
                      px: 1,
                      gap: 0.5,
                      borderRadius: 1,
                      transition: (theme) => theme.transitions.create("background-color"),
                      bgcolor:
                        highlightedHistoryId === entry.id && confirmHideId === null
                          ? (theme) => alpha(theme.palette.error.main, 0.14)
                          : "transparent",
                    }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center", gap: 3, flexWrap: "wrap", width: "100%" }}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 0.8, flexWrap: "wrap", flex: 1 }}>
                        <Chip
                          label={`${t("aircraft_management.enabled")}: ${entry.enabled ? "✓" : "✗"}`}
                          size="small"
                          color={entry.enabled ? "success" : "error"}
                          variant="outlined"
                        />
                        <Chip
                          label={`${t("aircraft_management.under_supervision")}: ${entry.underSupervision ? "✓" : "✗"}`}
                          size="small"
                          color={entry.underSupervision ? "success" : "error"}
                          variant="outlined"
                        />
                        {isHidden && (
                          <Chip label={t("aircraft_management.hidden_history_item")} size="small" color="default" />
                        )}
                      </Box>
                      {canHideEntry && (
                        <IconButton
                          size="small"
                          color="error"
                          disabled={isRefreshing || hidingIds.has(entry.id)}
                          title={t("aircraft_management.hide_history_item")}
                          onMouseMove={() => setHighlightedHistoryId(entry.id)}
                          onMouseLeave={() => setHighlightedHistoryId(null)}
                          onClick={() => {
                            setHighlightedHistoryId(null);
                            setConfirmHideId(entry.id);
                          }}
                        >
                          <DeleteForeverIcon fontSize="small" />
                        </IconButton>
                      )}
                    </Box>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.8, flexWrap: "wrap" }}>
                      <Typography
                        variant="body2"
                        sx={{
                          color: "text.secondary",
                          lineHeight: 1,
                        }}
                      >
                        {entry.changedByName} · {new Date(entry.changedAt).toLocaleString("en-GB", { hour12: false })}
                      </Typography>
                    </Box>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.8, flexWrap: "wrap" }}>
                      <Typography variant="body2">{t("limitations.title")}:</Typography>
                      {limitationDiffs.length > 0 ? (
                        limitationDiffs.map(({ limitation, state }) => (
                          <Chip
                            key={`${entry.id}-${limitation.definitionId}-${state}`}
                            label={limitation.displayValue}
                            size="small"
                            color={state === "added" ? "success" : state === "removed" ? "error" : "primary"}
                            variant="filled"
                            sx={{
                              height: 20,
                              fontSize: "0.7rem",
                              textDecoration: state === "removed" ? "line-through" : "none",
                            }}
                          />
                        ))
                      ) : (
                        <Typography
                          variant="body2"
                          sx={{
                            color: "text.secondary",
                          }}
                        >
                          {t("limitations.no_limitations")}
                        </Typography>
                      )}
                    </Box>
                    {canEdit && !isHidden ? (
                      <TextField
                        size="small"
                        label={editingComments[entry.id] ? undefined : t("aircraft_management.comment_placeholder")}
                        value={editingComments[entry.id] ?? ""}
                        onChange={(e) => handleCommentChange(entry.id, e.target.value)}
                        multiline
                        minRows={1}
                        variant="outlined"
                        fullWidth
                      />
                    ) : (
                      editingComments[entry.id] && (
                        <Typography
                          variant="body2"
                          sx={{
                            color: "text.secondary",
                            fontStyle: "italic",
                          }}
                        >
                          {editingComments[entry.id]}
                        </Typography>
                      )
                    )}
                  </ListItem>
                </React.Fragment>
              );
            })
          )}
        </List>
      </LoadingDialog>

      <Confirm
        open={confirmHideId !== null}
        title={t("aircraft_management.hide_history_confirm_title")}
        confirmColor="error"
        onConfirm={(result) => {
          const historyId = confirmHideId;
          setHighlightedHistoryId(null);
          setConfirmHideId(null);
          if (result && historyId !== null) {
            handleHideEntry(historyId);
          }
        }}
      >
        {t("aircraft_management.hide_history_confirm_message")}
      </Confirm>
    </>
  );
};

export default HistoryDialog;
